/**
 * Opérations techniques de la plateforme (SUPER_ADMIN).
 * Sections: état du système, sauvegardes, entretien des données, mémoire rapide,
 * connexions en direct, mises à jour techniques, codes d'accès, journal technique.
 */
import { Aide } from '@/components/ui/aide';
import React, { useEffect, useState } from 'react';
import {
  Database,
  Archive,
  RefreshCw,
  Trash2,
  Upload,
  Play,
  Key,
  Zap,
  FileText,
  Terminal,
  Wifi,
  Power,
  Plus,
} from 'lucide-react';
import { backendOpsAPI } from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { ConsolePanel } from '../components/ui/page-layouts';
import { libelle } from '../lib/libelles';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure, formatNombre, formatTaille } from '../lib/formats';

// Nom lisible d'une table de données: jamais le nom technique.
const nomTable = (nom) => libelle('tableDonnees', nom, 'Autre table');

const dureeLisible = (secondes) => {
  const total = Number(secondes) || 0;
  if (total < 3600) return `${Math.round(total / 60)} min`;
  if (total < 86400) return `${Math.round(total / 3600)} h`;
  return `${Math.round(total / 86400)} j`;
};

export default function BackendOpsPage() {
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [collections, setCollections] = useState([]);
  const [cache, setCache] = useState(null);
  const [wsInfo, setWsInfo] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [h, b, c, ca, w, m, l] = await Promise.allSettled([
        backendOpsAPI.getHealth(),
        backendOpsAPI.listBackups(),
        backendOpsAPI.collectionsStats(),
        backendOpsAPI.cacheStats(),
        backendOpsAPI.wsStats(),
        backendOpsAPI.listMigrations(),
        backendOpsAPI.logsTail(150),
      ]);
      if (h.status === 'fulfilled') setHealth(h.value);
      if (b.status === 'fulfilled') setBackups(b.value || []);
      if (c.status === 'fulfilled') setCollections(c.value || []);
      if (ca.status === 'fulfilled') setCache(ca.value);
      if (w.status === 'fulfilled') setWsInfo(w.value);
      if (m.status === 'fulfilled') setMigrations(m.value || []);
      if (l.status === 'fulfilled') setLogs(l.value?.lines || []);
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de charger les opérations techniques'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const withBusy = async (fn, successMsg = 'Opération effectuée') => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(successMsg);
      return r;
    } catch (e) {
      toast.error(messageErreur(e, 'L’opération a échoué'));
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const doCreateBackup = async () => {
    await withBusy(() => backendOpsAPI.createBackup(), 'Sauvegarde créée');
    loadAll();
  };

  const doRestore = async (name, quand) => {
    if (!window.confirm(`Restaurer la sauvegarde du ${quand} ? Cette action est irréversible: elle remplace toutes les données actuelles.`)) return;
    if (!window.confirm(`Confirmer une seconde fois : restaurer la sauvegarde du ${quand} ?`)) return;
    await withBusy(() => backendOpsAPI.restoreBackup(name), 'Restauration effectuée');
    loadAll();
  };

  const doDeleteBackup = async (name, quand) => {
    if (!window.confirm(`Supprimer la sauvegarde du ${quand} ?`)) return;
    await withBusy(() => backendOpsAPI.deleteBackup(name), 'Sauvegarde supprimée');
    loadAll();
  };

  const doPurgeSoft = async (days, dryRun) => {
    const res = await withBusy(() => backendOpsAPI.purgeSoftDeleted(days, dryRun), dryRun ? 'Simulation effectuée' : 'Nettoyage effectué');
    const nombre = Object.values(res?.deleted || {}).reduce((somme, n) => somme + (Number(n) || 0), 0);
    toast.info(
      dryRun
        ? `Simulation: ${formatNombre(nombre)} élément(s) seraient supprimés définitivement.`
        : `${formatNombre(nombre)} élément(s) supprimés définitivement.`,
    );
  };

  const doPurgeGps = async (days, dryRun) => {
    const res = await withBusy(() => backendOpsAPI.purgeGps(days, dryRun), dryRun ? 'Simulation effectuée' : 'Nettoyage des positions effectué');
    const nombre = Number(res?.deleted) || 0;
    toast.info(
      dryRun
        ? `Simulation: ${formatNombre(nombre)} position(s) seraient supprimées.`
        : `${formatNombre(nombre)} position(s) supprimées.`,
    );
  };

  const doReindex = async (coll) => {
    await withBusy(() => backendOpsAPI.reindex(coll), `Index reconstruits: ${nomTable(coll)}`);
    loadAll();
  };

  const doCacheFlush = async () => {
    if (!window.confirm('Vider toute la mémoire rapide ? Le système sera un peu plus lent quelques instants.')) return;
    await withBusy(() => backendOpsAPI.cacheFlush(), 'Mémoire rapide vidée');
    loadAll();
  };

  const doWsFlush = async () => {
    if (!window.confirm('Déconnecter toutes les connexions en direct actives ?')) return;
    await withBusy(() => backendOpsAPI.wsFlush(), 'Connexions en direct déconnectées');
    loadAll();
  };

  const doRunMigration = async (name, libelleMiseAJour) => {
    if (!window.confirm(`Lancer la mise à jour technique « ${libelleMiseAJour} » ?`)) return;
    const r = await withBusy(() => backendOpsAPI.runMigration(name), 'Mise à jour technique lancée');
    if (r?.exit_code === 0 || r?.exit_code === undefined) toast.info('La mise à jour s’est terminée sans erreur.');
    else toast.error('La mise à jour s’est terminée avec une erreur.');
  };

  const doRegenCodes = async () => {
    const days = prompt('Renouveler les codes plus anciens que combien de jours ?', '180');
    if (!days) return;
    await withBusy(() => backendOpsAPI.regenerateExpiredCodes(parseInt(days, 10)), 'Codes renouvelés');
  };

  const doExport = async (coll, format) => {
    try {
      const blob = await backendOpsAPI.exportCollection(coll, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${coll}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible d’exporter ces données'));
    }
  };

  const svc = health?.services || {};
  const sys = health?.system || {};

  // L'état du système tient dans le coin de l'en-tête: un chiffre par organe,
  // vert quand il va bien, rouge sinon; le détail (tailles, mémoire) est au
  // survol et dans « Plus d'indicateurs ».
  const etat = (ok) => (ok ? 'positif' : 'critique');
  const serviceValeur = (service) =>
    service?.latency_ms ? `${service.latency_ms} ms` : service?.status === 'up' ? 'En service' : 'Hors service';
  const detailBase = svc.mongodb?.data_size_mb ? `${svc.mongodb.data_size_mb} Mo` : undefined;
  const detailRapide = svc.redis?.used_memory_mb ? `${svc.redis.used_memory_mb} Mo` : undefined;
  const detailMemoire = `${sys.memory_used_mb?.toFixed?.(0) || '—'} sur ${sys.memory_total_mb?.toFixed?.(0) || '—'} Mo`;
  const detailDisque = `${sys.disk_used_gb?.toFixed?.(1) || '—'} sur ${sys.disk_total_gb?.toFixed?.(1) || '—'} Go`;
  const pourcent = (valeur) => (valeur != null ? `${valeur} %` : '—');
  const statsSysteme = [
    { label: 'Base de données', valeur: serviceValeur(svc.mongodb), ton: etat(svc.mongodb?.status === 'up'), aide: detailBase },
    { label: 'Mémoire rapide', valeur: serviceValeur(svc.redis), ton: etat(svc.redis?.status === 'up'), aide: detailRapide },
    { label: 'Processeur', valeur: pourcent(sys.cpu_percent), ton: etat((sys.cpu_percent ?? 0) < 80) },
    { label: 'Disque', valeur: pourcent(sys.disk_percent), ton: etat((sys.disk_percent ?? 0) < 85), aide: detailDisque },
  ];
  const statsPlusSysteme = (
    <dl className="space-y-1.5 text-sm">
      {[
        ['Base de données', detailBase],
        ['Mémoire rapide', detailRapide],
        ['Mémoire', `${pourcent(sys.memory_percent)} · ${detailMemoire}`],
        ['Disque', detailDisque],
      ].map(([libelleLigne, valeur]) => (
        <div key={libelleLigne} className="flex items-baseline justify-between gap-3">
          <dt className="text-muted-foreground">{libelleLigne}</dt>
          <dd className="text-right font-medium tabular-nums">{valeur || '—'}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Opérations techniques"
        description="Sauvegardes, entretien des données et état du système."
        stats={statsSysteme}
        statsPlus={statsPlusSysteme}
        alertesSousTitre
        alertes={[
          (sys.memory_percent ?? 0) >= 80 && {
            niveau: 'attention',
            court: 'Mémoire presque pleine',
            texte: `La mémoire du système est utilisée à ${sys.memory_percent} % (${detailMemoire}).`,
          },
        ]}
        actions={
          <button onClick={loadAll} className="btn-secondary px-3" disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </button>
        }
      />

      {/* Sauvegardes */}
      <Section icon={Archive} title="Sauvegardes" color="amber" actions={
        <button
          onClick={doCreateBackup}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" /> Nouvelle sauvegarde
        </button>
      }>
        {backups.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground/70">Aucune sauvegarde pour le moment.</div>
        ) : (
          <div className="space-y-2">
            {backups.map((b) => (
              <div key={b.name} className="flex items-center justify-between rounded-lg border border-border bg-muted/50 p-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">Sauvegarde du {formatDateHeure(b.created_at)}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.size_mb} Mo
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => doRestore(b.name, formatDateHeure(b.created_at))}
                    className="inline-flex items-center gap-1 rounded-md border border-process/30 bg-process/10 px-2 py-1 text-xs font-medium text-process hover:bg-process/15"
                  >
                    <Upload className="h-3.5 w-3.5" /> Restaurer
                  </button>
                  <button
                    onClick={() => doDeleteBackup(b.name, formatDateHeure(b.created_at))}
                    title="Supprimer cette sauvegarde"
                    aria-label="Supprimer cette sauvegarde"
                    className="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-card px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Entretien des données */}
      <Section icon={Database} title="Entretien des données" color="sky">
        <div className="grid gap-3 md:grid-cols-2">
          <Card title="Nettoyer les éléments supprimés" description="Supprime définitivement les éléments déjà supprimés depuis plus de 30 jours. « Simuler » indique seulement combien seraient supprimés.">
            <div className="flex gap-2">
              <button onClick={() => doPurgeSoft(30, true)} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/50">Simuler (30 jours)</button>
              <button onClick={() => doPurgeSoft(30, false)} className="rounded-md bg-destructive px-3 py-1.5 text-xs font-bold text-white hover:bg-destructive">Nettoyer (30 jours)</button>
            </div>
          </Card>
          <Card title="Nettoyer l’historique des positions" description="Supprime les positions des camions enregistrées il y a plus de 90 jours.">
            <div className="flex gap-2">
              <button onClick={() => doPurgeGps(90, true)} className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/50">Simuler (90 jours)</button>
              <button onClick={() => doPurgeGps(90, false)} className="rounded-md bg-destructive px-3 py-1.5 text-xs font-bold text-white hover:bg-destructive">Nettoyer (90 jours)</button>
            </div>
          </Card>
        </div>

        <div className="mt-4">
          <h3 className="mb-2 uppercase text-muted-foreground">Tables de données ({formatNombre(collections.length)})</h3>
          <div className="max-h-64 overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left readout-label">
                <tr>
                  <th className="px-3 py-2">Table</th>
                  <th className="px-3 py-2">Éléments</th>
                  <th className="px-3 py-2">Taille</th>
                  <th className="px-3 py-2">Index</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {collections.map((c) => (
                  <tr key={c.name} className="hover:bg-muted/50">
                    <td className="px-3 py-2 text-xs font-medium">{nomTable(c.name)}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{formatNombre(c.count)}</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{formatTaille(c.size_bytes)}</td>
                    <td className="px-3 py-2 text-xs">{c.indexes}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => doReindex(c.name)} className="rounded-md border border-border px-2 py-0.5 text-[11px] hover:bg-muted">Reconstruire les index</button>
                        <button onClick={() => doExport(c.name, 'csv')} className="rounded-md border border-border px-2 py-0.5 text-[11px] hover:bg-muted">Exporter (Excel)</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* Mémoire rapide et connexions en direct */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={Zap} title="Mémoire rapide" color="rose" actions={
          <button onClick={doCacheFlush} className="inline-flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/15">
            <Trash2 className="h-3.5 w-3.5" /> Vider
          </button>
        }>
          {cache ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <KV label="Éléments gardés" value={formatNombre(cache.keys ?? 0)} />
              <KV label="Mémoire utilisée" value={`${cache.used_memory_mb || 0} Mo`} />
              <KV label="Réponses trouvées" value={formatNombre(cache.hits ?? 0)} />
              <KV label="Réponses manquées" value={formatNombre(cache.misses ?? 0)} />
              <KV label="Connexions" value={formatNombre(cache.connected_clients ?? 0)} />
              <KV label="En marche depuis" value={dureeLisible(cache.uptime_seconds)} />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground/70">La mémoire rapide n’est pas disponible</div>
          )}
        </Section>

        <Section icon={Wifi} title="Connexions en direct" color="sky" actions={
          <button onClick={doWsFlush} className="inline-flex items-center gap-1 rounded-md border border-info/30 bg-info/10 px-2 py-1 text-xs font-medium text-info hover:bg-info/15">
            <Power className="h-3.5 w-3.5" /> Tout déconnecter
          </button>
        }>
          <div className="text-sm">
            <KV label="Connexions actives" value={formatNombre(wsInfo?.connections ?? 0)} />
          </div>
        </Section>
      </div>

      {/* Mises à jour techniques et codes d'accès */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={Terminal} title="Mises à jour techniques" color="violet">
          <div className="space-y-2">
            {migrations.length === 0 && <div className="text-sm text-muted-foreground/70">Aucune mise à jour technique disponible</div>}
            {migrations.map((m, index) => {
              const name = typeof m === 'string' ? m : m?.name;
              const nomAffiche = `Mise à jour technique n° ${index + 1}`;
              const exists = typeof m === 'object' ? m?.exists !== false : true;
              return (
                <div
                  key={name}
                  className="flex items-center justify-between rounded-lg border border-border bg-muted/50 p-2"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{nomAffiche}</div>
                    {!exists && (
                      <div className="text-[11px] font-semibold text-destructive">
                        Fichier introuvable
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => doRunMigration(name, nomAffiche)}
                    disabled={!exists}
                    className="inline-flex items-center gap-1 rounded-md bg-process px-2 py-1 text-xs font-bold text-white hover:bg-process disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="h-3.5 w-3.5" /> Lancer
                  </button>
                </div>
              );
            })}
          </div>
        </Section>

        <Section icon={Key} title="Codes d'accès" color="amber" aide="Renouvelle les codes d’accès des comptes de l’équipe plus anciens qu’un nombre de jours à choisir.">
          <button
            onClick={doRegenCodes}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            <RefreshCw className="h-4 w-4" /> Renouveler les anciens codes
          </button>
        </Section>
      </div>

      {/* Journal technique: réservé au support, masqué par défaut */}
      {!showLogs ? (
        <button
          type="button"
          onClick={() => setShowLogs(true)}
          className="btn-secondary px-3"
        >
          <FileText className="h-4 w-4" aria-hidden="true" /> Afficher le journal technique
        </button>
      ) : (
        <ConsolePanel
          title="Journal technique"
          actions={
            <span className="text-readout-foreground/50">
              {formatNombre(logs.length)} ligne{logs.length > 1 ? 's' : ''}
            </span>
          }
        >
          <pre className="whitespace-pre-wrap break-all">
{logs.length ? logs.join('\n') : '(aucune ligne disponible)'}
          </pre>
        </ConsolePanel>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, color = 'slate', children, actions, aide }) => {
  const colors = {
    emerald: 'text-success',
    amber: 'text-flame-ink',
    sky: 'text-info',
    violet: 'text-process',
    rose: 'text-destructive',
    slate: 'text-muted-foreground',
  };
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border/60 p-4">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${colors[color]}`} />
          <h2 className="uppercase text-muted-foreground">{title}</h2>
          {aide && <Aide titre={title}>{aide}</Aide>}
        </div>
        {actions}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

const Card = ({ title, description, children }) => (
  <div className="rounded-xl border border-border bg-muted/50 p-4">
    <div className="mb-3 flex items-center gap-1">
      <h4 className="text-sm font-bold text-foreground">{title}</h4>
      {description && <Aide titre={title}>{description}</Aide>}
    </div>
    {children}
  </div>
);


const KV = ({ label, value }) => (
  <div className="flex items-center justify-between rounded border border-border/60 px-2 py-1">
    <span className="text-xs font-medium text-muted-foreground">{label}</span>
    <span className="font-mono text-xs font-bold text-foreground">{value}</span>
  </div>
);
